import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn, formatCurrency } from '../../lib/utils';
import { Lightbulb, TrendingUp, AlertTriangle, DollarSign, Clock, CheckCircle2, ArrowRight, Sparkles, Target, Zap, Brain } from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_RECOMMENDATIONS = [
  {
    id: 1,
    category: 'revenue',
    priority: 'high',
    title: 'Unbilled Encounters Detected',
    description: '12 encounters from the past 7 days have not been converted to claims. Estimated revenue at risk: $4,850.',
    impact: 4850,
    action: 'Review Encounters',
    actionPath: '/revq/encounters',
    confidence: 98
  },
  {
    id: 2,
    category: 'denials',
    priority: 'high',
    title: 'Recurring Denial Pattern: CO-16',
    description: 'Blue Cross has denied 8 claims this month for missing documentation. Consider implementing pre-submission checklist.',
    impact: 3200,
    action: 'View Denials',
    actionPath: '/revq/denials',
    confidence: 95
  },
  {
    id: 3,
    category: 'coding',
    priority: 'medium',
    title: 'E/M Level Optimization',
    description: 'Dr. Wilson\'s coding patterns show 40% at 99213. Documentation review suggests 15% could support 99214 (+$45/visit).',
    impact: 2700,
    action: 'Review Cases',
    actionPath: '/revq/analytics',
    confidence: 87
  },
  {
    id: 4,
    category: 'ar',
    priority: 'medium',
    title: 'Aging A/R Alert: Aetna',
    description: '15 claims approaching 60-day mark with Aetna. Recommend immediate follow-up to prevent timely filing risk.',
    impact: 5400,
    action: 'View A/R',
    actionPath: '/revq/claims',
    confidence: 92
  },
  {
    id: 5,
    category: 'payer',
    priority: 'low',
    title: 'Contract Rate Discrepancy',
    description: 'UnitedHealth reimbursements for 99214 are 8% below contracted rate. Consider filing for adjustment.',
    impact: 1850,
    action: 'Review Contract',
    actionPath: '/revq/payers-rules',
    confidence: 78
  },
  {
    id: 6,
    category: 'workflow',
    priority: 'low',
    title: 'Claim Submission Timing',
    description: 'Claims submitted before 10 AM have 12% faster payment. Consider adjusting batch submission schedule.',
    impact: 0,
    action: 'Learn More',
    actionPath: '/revq/analytics',
    confidence: 82
  },
];

const RevQAIRecommendations = () => {
  const { isDemo } = useAppMode();
  const [filter, setFilter] = useState('all');

  const getCategoryIcon = (category) => {
    const icons = {
      revenue: DollarSign,
      denials: AlertTriangle,
      coding: Target,
      ar: Clock,
      payer: TrendingUp,
      workflow: Zap,
    };
    return icons[category] || Lightbulb;
  };

  const getCategoryColor = (category) => {
    const colors = {
      revenue: 'bg-emerald-50 text-emerald-600',
      denials: 'bg-red-50 text-red-600',
      coding: 'bg-violet-50 text-violet-600',
      ar: 'bg-amber-50 text-amber-600',
      payer: 'bg-blue-50 text-blue-600',
      workflow: 'bg-slate-100 text-slate-600',
    };
    return colors[category] || 'bg-slate-100 text-slate-600';
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      high: { label: 'High Priority', class: 'bg-red-100 text-red-700 border-red-200' },
      medium: { label: 'Medium', class: 'bg-amber-100 text-amber-700 border-amber-200' },
      low: { label: 'Low', class: 'bg-slate-100 text-slate-600 border-slate-200' },
    };
    return <Badge className={configs[priority]?.class}>{configs[priority]?.label}</Badge>;
  };

  const filteredRecommendations = filter === 'all' 
    ? DEMO_RECOMMENDATIONS 
    : DEMO_RECOMMENDATIONS.filter(r => r.priority === filter);

  const totalImpact = DEMO_RECOMMENDATIONS.reduce((sum, r) => sum + r.impact, 0);
  const highPriority = DEMO_RECOMMENDATIONS.filter(r => r.priority === 'high').length;

  return (
    <div className="space-y-6" data-testid="ai-recommendations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">AI Recommendations</h1>
            <Badge className="bg-violet-100 text-violet-700 border-violet-200">
              <Brain className="h-3 w-3 mr-1" /> Echo-Powered
            </Badge>
          </div>
          <p className="text-slate-500 mt-1">Intelligent insights to optimize your revenue cycle</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-violet-600 font-medium">Active Recommendations</p>
                <p className="text-3xl font-bold text-violet-700">{DEMO_RECOMMENDATIONS.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Potential Recovery</p>
                <p className="text-3xl font-bold text-emerald-700">{formatCurrency(totalImpact)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">High Priority</p>
                <p className="text-3xl font-bold text-red-700">{highPriority}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'high', 'medium', 'low'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={cn(filter === f && 'bg-black')}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {filteredRecommendations.map((rec) => {
          const Icon = getCategoryIcon(rec.category);
          return (
            <Card key={rec.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0", getCategoryColor(rec.category))}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{rec.title}</h3>
                          {getPriorityBadge(rec.priority)}
                        </div>
                        <p className="text-slate-600 mb-3">{rec.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          {rec.impact > 0 && (
                            <span className="flex items-center gap-1 text-emerald-600 font-medium">
                              <DollarSign className="h-4 w-4" />
                              {formatCurrency(rec.impact)} potential
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-slate-500">
                            <Sparkles className="h-4 w-4" />
                            {rec.confidence}% confidence
                          </span>
                        </div>
                      </div>
                      <Button className="bg-black hover:bg-gray-800 gap-2 flex-shrink-0">
                        {rec.action} <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Learning Note */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Brain className="h-5 w-5 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">RevQ AI continuously learns from your data</p>
              <p className="text-sm text-slate-500">Recommendations improve as we analyze your billing patterns, payer behaviors, and outcomes.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevQAIRecommendations;
